import { useEffect, useRef, useCallback } from "react";
import "@/App.css";
import Lenis from "lenis";
import {
  BrowserRouter, Routes, Route, useLocation, useNavigate,
} from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import { ALIAS_TO_SLUG } from "@/servicesData";

function Shell() {
  const lenisRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollToEl = useCallback((selector) => {
    if (selector === "#top") {
      lenisRef.current ? lenisRef.current.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(selector);
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Scroll to top on route (pathname) change
  useEffect(() => {
    window.scrollTo(0, 0);
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
  }, [location.pathname]);

  // Deferred scroll requested when navigating from another page
  useEffect(() => {
    const target = location.state && location.state.scrollTo;
    if (target) {
      const t = setTimeout(() => scrollToEl(target), 250);
      return () => clearTimeout(t);
    }
  }, [location, scrollToEl]);

  const onNav = useCallback((selector) => {
    if (location.pathname === "/") {
      scrollToEl(selector);
    } else {
      navigate("/", { state: { scrollTo: selector } });
    }
  }, [location.pathname, navigate, scrollToEl]);

  return (
    <div className="App grain bg-[#050505]">
      <Header onNav={onNav} />
      <main>
        <Routes>
          <Route path="/" element={<Home onNav={onNav} />} />
          <Route path="/services/:slug" element={<ServicePage onNav={onNav} />} />
          {Object.entries(ALIAS_TO_SLUG).map(([alias, slug]) => (
            <Route
              key={alias}
              path={`/${alias}`}
              element={<ServicePage onNav={onNav} aliasSlug={slug} />}
            />
          ))}
          <Route path="*" element={<Home onNav={onNav} />} />
        </Routes>
      </main>
      <Footer onNav={onNav} />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#121212",
            border: "1px solid #2a2a2a",
            borderRadius: 0,
            color: "#f5f5f0",
          },
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}
